import { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { debounce } from '@mui/material/utils';

interface SearchInputProps {
  onSearch: (value: string) => void;
  placeholder: string;
  value?: string;
}

export const SearchInput = ({ onSearch, placeholder, value }: SearchInputProps) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState(value ?? '');
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  useEffect(() => {
    if (value !== undefined) setSearchValue(value);
  }, [value]);

  const debouncedSearch = useMemo(() => debounce((next: string) => onSearchRef.current(next), 300), []);

  useEffect(() => {
    if (value === undefined || searchValue !== value) debouncedSearch(searchValue);

    return () => {
      debouncedSearch.clear();
    };
  }, [searchValue, value, debouncedSearch]);

  return (
    <TextField
      variant="outlined"
      placeholder={placeholder}
      value={searchValue}
      onChange={(e) => setSearchValue(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      sx={{
        backgroundColor: theme.palette.primaryAlternative.lighter,
        width: '100%',
        maxWidth: theme.spacing(40),
        '& input': {
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} 0`,
        },
      }}
    />
  );
};
